import TextArea from "sap/m/TextArea";
import { MetadataOptions } from "sap/ui/core/Element";
import IconPool from "sap/ui/core/IconPool";
// @ts-expect-error TextAreaRenderer is no public module..
import TextAreaRenderer from "sap/m/TextAreaRenderer";
import { Button$PressEvent } from "sap/m/Button";
import Icon from "sap/ui/core/Icon";
import MessageBox from "sap/m/MessageBox";
import { Event } from "jquery";
import {
  SpeechRecognizer,
  Recognizer,
  SpeechRecognitionEventArgs,
  SpeechRecognitionCanceledEventArgs,
  SessionEventArgs,
} from "../../libs/microsoft.cognitive.speech.sdk/microsoft.cognitiveservices.speech.sdk.bundle";
import Log from "sap/base/Log";
import SpeechService from "../../service/Speech.service";

declare global {
  interface Window {
    SpeechSDK: any;
  }
}

/**
 * @name com.unitrade.crm.controls.speech.TextAreaWithSpeechRecognitions
 */
export default class TextAreaWithSpeechRecognition extends TextArea {
  private micIcon: Icon;
  private speechSdk: any;
  private recognizer: SpeechRecognizer;
  private currentRecognition: { ongoing: string; final: string };
  private logger = Log.getLogger("com.unitrade.crm.controls.speech.TextAreaWithSpeechRecognitions");
  private stopwatch: Date;
  private speechService: SpeechService;

  // The following three lines were generated and should remain as-is to make TypeScript aware of the constructor signatures
  constructor(idOrSettings?: string | $TextAreaWithSpeechRecognitionSettings);
  constructor(id?: string, settings?: $TextAreaWithSpeechRecognitionSettings);
  constructor(id?: string, settings?: $TextAreaWithSpeechRecognitionSettings) {
    super(id, settings);
  }

  static readonly metadata: MetadataOptions = {
    properties: {},
    events: {
      micButtonPress: {},
    },
  };
  static renderer = TextAreaRenderer;

  override async init(): Promise<void> {
    this.speechService = new SpeechService();
    this.currentRecognition = {
      final: "",
      ongoing: "",
    };

    this.micIcon = this.addEndIcon(
      {
        id: this.getId() + "-microphone",
        src: IconPool.getIconURI("microphone"),
        noTabStop: true,
        decorative: false,
        tooltip: "Spracherkennung",
        press: [this.onMicrophoneButtonPress, this],
      },
      0
    );

    if (window.SpeechSDK) {
      this.speechSdk = window.SpeechSDK;
    } else {
      MessageBox.warning("Fehler beim Laden vom Microsoft Azure Speech SDK. Spracherkennung nicht verfügbar.");
    }

    this.onRecognizing = this.onRecognizing.bind(this);
    this.onRecognized = this.onRecognized.bind(this);
    this.onCanceled = this.onCanceled.bind(this);
    this.onSessionStarted = this.onSessionStarted.bind(this);
    this.onSessionStopped = this.onSessionStopped.bind(this);
    super.init();
  }

  override onBeforeRendering(oEvent: Event): void {
    const endIcons = this.getAggregation("_endIcon");
    const isEditable = this.getEditable();

    if (Array.isArray(endIcons)) {
      endIcons.map((icon) => icon.setProperty("visible", isEditable, true));
    }

    this.addStyleClass("textarea-with-speech-recognition");
    super.onBeforeRendering(oEvent);
  }

  async onMicrophoneButtonPress(oEvent: Button$PressEvent) {
    if (!this.isRecognizing()) {
      try {
        this.addStyleClass("initializing");

        if (!this.recognizer) {
          this.recognizer = await this.speechService.getSpeechRecognizer();

          this.recognizer.recognizing = this.onRecognizing;
          this.recognizer.recognized = this.onRecognized;
          this.recognizer.canceled = this.onCanceled;
          this.recognizer.sessionStarted = this.onSessionStarted;
          this.recognizer.sessionStopped = this.onSessionStopped;
        }
        this.recognizer.startContinuousRecognitionAsync();
      } catch (ex: any) {
        MessageBox.show(ex);
      }
    } else {
      this.recognizer.stopContinuousRecognitionAsync();
    }
  }

  private onRecognizing(sender: Recognizer, event: SpeechRecognitionEventArgs) {
    if (event.result.text) {
      this.currentRecognition.ongoing = event.result.text;
      this.setValue(this.currentRecognition.final + this.currentRecognition.ongoing + "[...]");
    }
  }

  private onRecognized(sender: Recognizer, event: SpeechRecognitionEventArgs) {
    if (event.result.text) {
      this.currentRecognition.ongoing = "";
      this.currentRecognition.final += event.result.text + " ";
      this.setValue(this.currentRecognition.final + this.currentRecognition.ongoing + "[...]");
    }
  }

  private onCanceled(sender: Recognizer, event: SpeechRecognitionCanceledEventArgs) {
    this.removeStyleClass("recording");
    this.removeStyleClass("initializing");
    MessageBox.warning("Azure Spracherkennung abgebrochen", { details: event.errorDetails });
    this.recognizer = null;
  }

  private onSessionStarted(sender: Recognizer, event: SessionEventArgs) {
    this.logger.info("azure audio transcription started");
    this.removeStyleClass("initializing");
    this.addStyleClass("recording");
    this.stopwatch = new Date();
    this.currentRecognition = {
      ongoing: "",
      final: this.getValue(),
    };
    this.setValue(this.currentRecognition.final + "[...]");
  }

  private onSessionStopped(sender: Recognizer, event: SessionEventArgs) {
    const stopTime = new Date();
    const seconds = (stopTime.getTime() - this.stopwatch.getTime()) / 1000;
    this.logger.info(`azure audio transcription stopped (${seconds} seconds recorded)`);
    this.removeStyleClass("recording");
    this.setValue(this.currentRecognition.final);
  }

  private isRecognizing(): boolean {
    return this.hasStyleClass("recording");
  }
}
